import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadCloud, FolderUp, Trash2, CheckCircle2, Loader2, AlertTriangle, User, Car, HelpCircle } from "lucide-react";

const TECH_CATS = { pos: "POS", attestato: "Attestato / Corso", documento_identita: "Documento d'identità", patente: "Patente", altro: "Altro" };
const VEH_CATS = { libretto: "Libretto", assicurazione: "Assicurazione", bollo: "Bollo", revisione: "Revisione", altro: "Altro" };
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.xlsx,.xlsm,.docx,.txt,.csv,.doc,.xls";
const CONCURRENCY = 3;

async function readEntry(entry, prefix = "") {
  if (entry.isFile) {
    const f = await new Promise((res, rej) => entry.file(res, rej));
    return [{ file: f, path: prefix + f.name }];
  }
  const reader = entry.createReader();
  const all = [];
  let batch;
  do {
    batch = await new Promise((res, rej) => reader.readEntries(res, rej));
    all.push(...batch);
  } while (batch.length);
  const nested = await Promise.all(all.map((e) => readEntry(e, `${prefix}${entry.name}/`)));
  return nested.flat();
}

export default function BulkDocumentiPage() {
  const [rows, setRows] = useState([]);
  const [techs, setTechs] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const dirRef = useRef(null);
  const queueRef = useRef([]);
  const activeRef = useRef(0);

  useEffect(() => {
    api.get("/users", { params: { role: "tecnico,subappalto" } }).then((r) => setTechs(r.data || [])).catch(() => {});
    api.get("/vehicles").then((r) => setVehicles(r.data || [])).catch(() => {});
    dirRef.current?.setAttribute("webkitdirectory", "");
  }, []);

  const patch = (key, upd) => setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...upd } : r)));

  const pump = () => {
    while (activeRef.current < CONCURRENCY && queueRef.current.length) {
      const item = queueRef.current.shift();
      activeRef.current += 1;
      patch(item.key, { status: "analyzing" });
      const fd = new FormData();
      fd.append("file", item.file, item.file.name);
      fd.append("relative_path", item.path);
      api.post("/bulk-documents/analyze", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 })
        .then((r) => patch(item.key, { ...r.data, status: "ready" }))
        .catch((e) => patch(item.key, { status: "error", error: formatApiErrorDetail(e.response?.data?.detail) || "Caricamento non riuscito" }))
        .finally(() => { activeRef.current -= 1; pump(); });
    }
  };

  const enqueue = (items) => {
    const next = items.map(({ file, path }) => ({ key: `${Date.now()}-${Math.random()}`, file, path, status: "queued" }));
    setRows((prev) => [...prev, ...next]);
    queueRef.current.push(...next);
    pump();
  };

  const onPick = (e) => {
    enqueue(Array.from(e.target.files || []).map((f) => ({ file: f, path: f.webkitRelativePath || f.name })));
    e.target.value = "";
  };

  const onDrop = async (e) => {
    e.preventDefault();
    const entries = Array.from(e.dataTransfer.items || []).map((i) => i.webkitGetAsEntry?.()).filter(Boolean);
    if (entries.length) enqueue((await Promise.all(entries.map((en) => readEntry(en)))).flat());
    else enqueue(Array.from(e.dataTransfer.files || []).map((f) => ({ file: f, path: f.name })));
  };

  const removeRow = (row) => {
    queueRef.current = queueRef.current.filter((q) => q.key !== row.key);
    if (row.staging_id) api.delete(`/bulk-documents/staging/${row.staging_id}`).catch(() => {});
    setRows((prev) => prev.filter((r) => r.key !== row.key));
  };

  const readyRows = rows.filter((r) => r.status === "ready" && r.target_type !== "none" && r.target_id);
  const pending = rows.filter((r) => r.status === "queued" || r.status === "analyzing").length;
  const unassigned = rows.filter((r) => r.status === "ready" && (r.target_type === "none" || !r.target_id)).length;

  const confirm = async () => {
    setSaving(true);
    try {
      const items = readyRows.map((r) => ({
        staging_id: r.staging_id, target_type: r.target_type, target_id: r.target_id, category: r.category,
        title: r.title, issued_date: r.issued_date || null, expiry_date: r.expiry_date || null,
      }));
      const { data } = await api.post("/bulk-documents/confirm", { items });
      const failed = new Set((data.errors || []).map((e) => e.staging_id));
      setRows((prev) => prev.filter((r) => !items.some((i) => i.staging_id === r.staging_id) || failed.has(r.staging_id)));
      toast.success(`Salvati ${data.saved_technician} documenti tecnici e ${data.saved_vehicle} documenti mezzi${data.deadlines_created ? ` · ${data.deadlines_created} scadenze create` : ""}`);
      if (failed.size) toast.error(`${failed.size} documenti non salvati`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="bulk-docs-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Documenti</div>
        <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">Inserimento Documenti Massivi</h1>
        <p className="mt-2 max-w-3xl text-sm text-slate-600">
          Carica file o intere cartelle (es. <b>"Mario Rossi"</b> o <b>"AA123BB"</b>). Il sistema riconosce dal nome e, con l'AI,
          dal contenuto a chi appartiene ogni documento: quelli dei tecnici vanno in <b>Magazzino Viaggiante → I miei documenti</b>,
          quelli dei mezzi in <b>Automezzi</b> sulla targa corrispondente. Controlla l'anteprima e premi <b>Conferma</b>.
        </p>
      </div>

      <Card
        className="rounded-sm border-2 border-dashed border-slate-300 bg-white p-6 transition-colors hover:border-[#0052FF]"
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        data-testid="bulk-docs-dropzone"
      >
        <div className="flex flex-col items-center gap-3 text-center">
          <UploadCloud className="h-10 w-10 text-slate-400" />
          <div className="text-sm text-slate-600">Trascina qui file o cartelle</div>
          <div className="flex flex-wrap justify-center gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => fileRef.current?.click()} data-testid="bulk-docs-pick-files">
              <UploadCloud className="mr-2 h-4 w-4" /> Seleziona file
            </Button>
            <Button variant="outline" className="rounded-sm" onClick={() => dirRef.current?.click()} data-testid="bulk-docs-pick-folder">
              <FolderUp className="mr-2 h-4 w-4" /> Seleziona cartella
            </Button>
          </div>
          <div className="text-xs text-slate-500">PDF · Immagini · Excel · Word — max 20 MB per file</div>
          <input ref={fileRef} type="file" multiple accept={ACCEPT} onChange={onPick} className="hidden" data-testid="bulk-docs-file-input" />
          <input ref={dirRef} type="file" multiple onChange={onPick} className="hidden" data-testid="bulk-docs-folder-input" />
        </div>
      </Card>

      {rows.length > 0 && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap gap-3 text-sm text-slate-600" data-testid="bulk-docs-summary">
              <span><b>{rows.length}</b> file</span>
              {pending > 0 && <span className="flex items-center gap-1 text-[#0052FF]"><Loader2 className="h-4 w-4 animate-spin" /> {pending} in analisi</span>}
              <span className="text-emerald-700"><b>{readyRows.length}</b> pronti</span>
              {unassigned > 0 && <span className="text-amber-700"><b>{unassigned}</b> da assegnare</span>}
            </div>
            <Button onClick={confirm} disabled={saving || !readyRows.length} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="bulk-docs-confirm">
              <CheckCircle2 className="mr-2 h-4 w-4" /> {saving ? "Salvataggio…" : `Conferma (${readyRows.length})`}
            </Button>
          </div>
          <div className="space-y-2">
            {rows.map((r, i) => (
              <DocRow key={r.key} idx={i} row={r} techs={techs} vehicles={vehicles} onChange={(u) => patch(r.key, u)} onRemove={() => removeRow(r)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DocRow({ row, idx, techs, vehicles, onChange, onRemove }) {
  const busy = row.status === "queued" || row.status === "analyzing";
  const cats = row.target_type === "vehicle" ? VEH_CATS : TECH_CATS;
  const setType = (t) => onChange({ target_type: t, target_id: null, category: "altro" });
  return (
    <Card className="rounded-sm border border-slate-200 bg-white p-3" data-testid={`bulk-doc-row-${idx}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-[200px] flex-1">
          <div className="truncate text-sm font-semibold text-slate-800" title={row.path}>{row.file?.name || row.filename}</div>
          <div className="truncate text-xs text-slate-500">{row.path}</div>
          <StatusBadge row={row} idx={idx} />
        </div>
        {!busy && row.status !== "error" && (
          <div className="grid flex-[3] grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5">
            <Select value={row.target_type} onValueChange={setType}>
              <SelectTrigger className="rounded-sm" data-testid={`bulk-doc-type-${idx}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="technician">Tecnico</SelectItem>
                <SelectItem value="vehicle">Automezzo</SelectItem>
                <SelectItem value="none">Da assegnare</SelectItem>
              </SelectContent>
            </Select>
            <Select value={row.target_id || ""} onValueChange={(v) => onChange({ target_id: v, match_source: null })} disabled={row.target_type === "none"}>
              <SelectTrigger className="rounded-sm" data-testid={`bulk-doc-target-${idx}`}><SelectValue placeholder="Scegli…" /></SelectTrigger>
              <SelectContent>
                {(row.target_type === "vehicle" ? vehicles.map((v) => [v.id, `${v.plate}${v.name ? ` · ${v.name}` : ""}`]) : techs.map((t) => [t.id, t.name]))
                  .map(([id, label]) => <SelectItem key={id} value={id}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={cats[row.category] ? row.category : "altro"} onValueChange={(v) => onChange({ category: v })}>
              <SelectTrigger className="rounded-sm" data-testid={`bulk-doc-category-${idx}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(cats).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input value={row.title || ""} onChange={(e) => onChange({ title: e.target.value })} placeholder="Titolo" className="rounded-sm" data-testid={`bulk-doc-title-${idx}`} />
            <Input type="date" value={row.expiry_date || ""} onChange={(e) => onChange({ expiry_date: e.target.value })} className="rounded-sm" title="Scadenza" data-testid={`bulk-doc-expiry-${idx}`} />
          </div>
        )}
        <Button variant="ghost" size="icon" onClick={onRemove} disabled={row.status === "analyzing"} data-testid={`bulk-doc-remove-${idx}`} title="Rimuovi">
          <Trash2 className="h-4 w-4 text-slate-500" />
        </Button>
      </div>
    </Card>
  );
}

function StatusBadge({ row, idx }) {
  let cls = "bg-slate-100 text-slate-600", Icon = Loader2, label = "In coda";
  if (row.status === "analyzing") { cls = "bg-blue-50 text-[#0052FF]"; label = "Analisi AI…"; }
  else if (row.status === "error") { cls = "bg-red-50 text-red-700"; Icon = AlertTriangle; label = row.error; }
  else if (row.status === "ready" && (row.target_type === "none" || !row.target_id)) { cls = "bg-amber-50 text-amber-700"; Icon = HelpCircle; label = row.ai_error || "Da assegnare"; }
  else if (row.status === "ready") {
    cls = "bg-emerald-50 text-emerald-700"; Icon = row.target_type === "vehicle" ? Car : User;
    label = row.match_source === "nome" ? "Riconosciuto dal nome" : row.match_source === "ai" ? "Riconosciuto dall'AI" : "Assegnato a mano";
  }
  return (
    <span className={`mt-1 inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-[11px] font-semibold ${cls}`} data-testid={`bulk-doc-status-${idx}`}>
      {Icon && <Icon className={`h-3 w-3 ${row.status === "analyzing" || row.status === "queued" ? "animate-spin" : ""}`} />} {label}
    </span>
  );
}
